import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  ErrorMessage,
  InputLabel,
  Select,
  TextField,
  toast,
  Typography,
} from "@suraasa/placebo-ui"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import api from "api"
import { Document, DocumentTag } from "api/resources/documents/types"
import { APIError } from "api/utils"
import { Xmark } from "iconoir-react"
import { useState } from "react"

const ALLOWED_EXTENSIONS = [
  "pdf",
  "doc",
  "docx",
  "xls",
  "xlsx",
  "ppt",
  "pptx",
  "csv",
  "txt",
  "png",
  "jpg",
  "jpeg",
]
const MAX_SIZE = 25 * 1024 * 1024

type FieldErrors = Partial<Record<"file" | "tags" | "name", string[]>>

const validateFile = (file: File): string | null => {
  const extension = file.name.split(".").pop()?.toLowerCase() ?? ""
  if (!ALLOWED_EXTENSIONS.includes(extension))
    return `Allowed file types: ${ALLOWED_EXTENSIONS.join(", ")}`
  if (file.size > MAX_SIZE) return "The file should be at most 25 MB"
  return null
}

const toList = (v: string[] | string | undefined) =>
  v === undefined ? undefined : Array.isArray(v) ? v : [v]

const errorText = (e: unknown) => {
  if (!(e instanceof APIError)) return "Something went wrong"
  const { message, fieldErrors } = e.errors
  return (
    message ||
    toList(fieldErrors?.file)?.[0] ||
    toList(fieldErrors?.tags)?.[0] ||
    toList(fieldErrors?.name)?.[0] ||
    "Something went wrong"
  )
}

/** A file waiting to be uploaded, with the tags chosen for it */
type PendingUpload = { file: File; tags: DocumentTag[] }

type Props = {
  open: boolean
  onClose: () => void
  /** Edit this document; upload a new one when omitted */
  document?: Document
}

const DocumentDialog = ({ open, onClose, document }: Props) => {
  const queryClient = useQueryClient()
  const isEdit = !!document

  // Edit: one replacement file. Upload: any number, each becomes its own document
  const [file, setFile] = useState<File | null>(null)
  const [files, setFiles] = useState<PendingUpload[]>([])
  // Problems per file (missing tags, failed upload), by file name
  const [fileErrors, setFileErrors] = useState<Record<string, string>>({})
  const [uploading, setUploading] = useState(false)
  const [name, setName] = useState(document?.name ?? "")
  const [tags, setTags] = useState<DocumentTag[]>(document?.tags ?? [])
  const [errors, setErrors] = useState<FieldErrors>({})
  // Escape should close the open tags menu, not the whole dialog
  const [tagsMenuOpen, setTagsMenuOpen] = useState(false)

  const tagsQuery = useQuery({
    queryKey: ["document-tags"],
    queryFn: () => api.documents.tags(),
    enabled: open,
  })

  const setFileTags = (fileName: string, next: DocumentTag[]) => {
    setFiles(prev =>
      prev.map(u => (u.file.name === fileName ? { ...u, tags: next } : u))
    )
    if (next.length > 0)
      setFileErrors(prev => {
        const rest = { ...prev }
        delete rest[fileName]
        return rest
      })
  }

  const tagSelect = (
    value: DocumentTag[],
    onChange: (next: DocumentTag[]) => void
  ) => (
    <Select<DocumentTag, true>
      isMulti
      options={tagsQuery.data ?? []}
      isLoading={tagsQuery.isLoading}
      value={value}
      onChange={v => onChange([...v])}
      getOptionLabel={t => t.label}
      getOptionValue={t => t.value}
      placeholder="Choose tags"
      isDisabled={uploading}
      onMenuOpen={() => setTagsMenuOpen(true)}
      onMenuClose={() => setTagsMenuOpen(false)}
      // Render above the dialog so its scroll area doesn't clip the menu
      menuPortalTarget={window.document.body}
    />
  )

  const refreshList = () =>
    queryClient.invalidateQueries({ queryKey: ["documents"] })

  const { mutate, isPending } = useMutation({
    mutationFn: (data: FormData) =>
      document
        ? api.documents.update({
            urlParams: { documentId: document.uuid },
            data,
          })
        : api.documents.create({ data }),
    onSuccess: () => {
      toast.success(isEdit ? "Document updated" : "Document uploaded")
      refreshList()
      onClose()
    },
    onError: e => {
      if (!(e instanceof APIError)) {
        toast.error("Something went wrong. Please try again later.")
        return
      }
      const { message, fieldErrors } = e.errors
      // 403: someone else uploaded it. 404: it was deleted. Either way the list is stale
      if (e.statusCode === 403 || e.statusCode === 404) {
        toast.error(
          message ||
            (e.statusCode === 404
              ? "This document no longer exists"
              : "You can't change this document")
        )
        refreshList()
        onClose()
        return
      }
      if (message) toast.error(message)
      if (fieldErrors)
        setErrors({
          file: toList(fieldErrors.file),
          tags: toList(fieldErrors.tags),
          name: toList(fieldErrors.name),
        })
      if (!message && !fieldErrors)
        toast.error("Something went wrong. Please try again later.")
    },
  })

  // The API takes one file per request, so each file is uploaded on its own with its own tags
  const uploadAll = async () => {
    setUploading(true)
    const trimmedName = name.trim()
    const failed: Record<string, string> = {}
    let uploaded = 0

    for (const { file: f, tags: fileTags } of files) {
      const data = new FormData()
      data.append("file", f)
      // A custom name only makes sense for a single file; otherwise each keeps its file name
      if (files.length === 1 && trimmedName) data.append("name", trimmedName)
      fileTags.forEach(t => data.append("tags", t.value))
      try {
        await api.documents.create({ data })
        uploaded += 1
      } catch (e) {
        failed[f.name] = errorText(e)
      }
    }

    setUploading(false)
    if (uploaded > 0) refreshList()

    if (Object.keys(failed).length === 0) {
      toast.success(
        uploaded === 1 ? "Document uploaded" : `${uploaded} documents uploaded`
      )
      onClose()
      return
    }

    // Keep only the files that failed, so a retry doesn't upload the others twice
    setFiles(prev => prev.filter(u => u.file.name in failed))
    setFileErrors(failed)
    toast.error(
      uploaded > 0
        ? `${uploaded} uploaded, ${Object.keys(failed).length} failed`
        : "The upload failed"
    )
  }

  const onSubmit = () => {
    const nextErrors: FieldErrors = {}
    if (!isEdit && files.length === 0)
      nextErrors.file = ["Please choose at least one file"]
    if (isEdit && tags.length === 0)
      nextErrors.tags = ["Please choose at least one tag"]
    if (isEdit && !name.trim()) nextErrors.name = ["Please enter a name"]
    const missingTags = isEdit
      ? []
      : files.filter(u => u.tags.length === 0).map(u => u.file.name)
    setErrors(nextErrors)
    setFileErrors(
      Object.fromEntries(
        missingTags.map(n => [n, "Please choose at least one tag"])
      )
    )
    if (Object.keys(nextErrors).length > 0 || missingTags.length > 0) return

    if (!isEdit) {
      uploadAll()
      return
    }

    const data = new FormData()
    const trimmedName = name.trim()
    const tagsChanged =
      !document ||
      tags.map(t => t.value).join() !== document.tags.map(t => t.value).join()

    if (file) data.append("file", file)
    // Upload: name only if typed (hades defaults to the file name). Edit: only if changed
    if (trimmedName && trimmedName !== document?.name)
      data.append("name", trimmedName)
    // Tags replace the whole list, so every remaining tag is sent
    if (tagsChanged) tags.forEach(t => data.append("tags", t.value))

    if (isEdit && [...data.keys()].length === 0) {
      onClose()
      return
    }
    mutate(data)
  }

  return (
    <Dialog open={open} onOpenChange={o => !o && !uploading && onClose()}>
      <DialogContent
        onEscapeKeyDown={e => {
          if (tagsMenuOpen) e.preventDefault()
        }}
      >
        <DialogBody>
          <Typography variant="title3" className="mb-2">
            {isEdit ? "Edit document" : "Upload documents"}
          </Typography>

          <div className="flex flex-col gap-2">
            <div className="flex flex-col gap-0.5">
              <InputLabel
                label={
                  isEdit
                    ? "Replace file (optional)"
                    : "Files (choose tags for each)"
                }
              />
              <input
                type="file"
                multiple={!isEdit}
                accept={ALLOWED_EXTENSIONS.map(e => `.${e}`).join(",")}
                onChange={e => {
                  const picked = [...(e.target.files ?? [])]
                  e.target.value = ""
                  const invalid = picked
                    .map(f => [f.name, validateFile(f)] as const)
                    .filter(([, error]) => error)
                  setErrors(prev => ({
                    ...prev,
                    file:
                      invalid.length > 0
                        ? invalid.map(([n, error]) => `${n}: ${error}`)
                        : undefined,
                  }))
                  const valid = picked.filter(f => !validateFile(f))
                  if (isEdit) {
                    setFile(valid[0] ?? null)
                    return
                  }
                  // Adds to the files already chosen; the same name isn't added twice
                  setFiles(prev => [
                    ...prev,
                    ...valid
                      .filter(f => !prev.some(p => p.file.name === f.name))
                      .map(f => ({ file: f, tags: [] })),
                  ])
                }}
              />
              {isEdit && file && (
                <Typography variant="smallBody">{file.name}</Typography>
              )}
              {!isEdit && files.length > 0 && (
                <ul className="flex flex-col gap-1">
                  {files.map(({ file: f, tags: fileTags }) => (
                    <li
                      key={f.name}
                      className="flex flex-col gap-0.5 rounded border border-onSurface-200 p-1"
                    >
                      <div className="flex items-center justify-between gap-1">
                        <Typography
                          variant="strongSmallBody"
                          className="truncate"
                        >
                          {f.name}
                        </Typography>
                        <button
                          type="button"
                          aria-label={`Remove ${f.name}`}
                          disabled={uploading}
                          onClick={() => {
                            setFiles(prev => prev.filter(u => u.file !== f))
                            setFileErrors(prev => {
                              const next = { ...prev }
                              delete next[f.name]
                              return next
                            })
                          }}
                        >
                          <Xmark className="size-2 text-onSurface-500" />
                        </button>
                      </div>
                      {tagSelect(fileTags, next => setFileTags(f.name, next))}
                      {fileErrors[f.name] && (
                        <Typography
                          variant="smallBody"
                          className="text-critical-500"
                        >
                          {fileErrors[f.name]}
                        </Typography>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              {isEdit && !file && (
                <Typography variant="smallBody" className="text-onSurface-500">
                  Current file: {document.name}
                </Typography>
              )}
              <ErrorMessage title={errors.file} />
            </div>

            {/* Uploads pick tags per file, in the list above */}
            {isEdit && (
              <div className="flex flex-col gap-0.5">
                <InputLabel label="Tags" />
                {tagSelect(tags, setTags)}
                <ErrorMessage title={errors.tags} />
              </div>
            )}

            {/* With several files, each document is named after its file */}
            {(isEdit || files.length <= 1) && (
              <TextField
                label={isEdit ? "Name" : "Name (optional)"}
                placeholder={isEdit ? undefined : "Defaults to the file name"}
                value={name}
                onChange={e => setName(e.target.value)}
                errors={errors.name}
              />
            )}
            {!isEdit && files.length > 1 && (
              <Typography variant="smallBody" className="text-onSurface-500">
                Each document will be named after its file. You can rename them
                after uploading.
              </Typography>
            )}
          </div>

          <div className="mt-3 flex justify-end gap-1">
            <Button variant="text" disabled={uploading} onClick={onClose}>
              Cancel
            </Button>
            <Button loading={isPending || uploading} onClick={onSubmit}>
              {isEdit
                ? "Save"
                : files.length > 1
                ? `Upload ${files.length} documents`
                : "Upload"}
            </Button>
          </div>
        </DialogBody>
      </DialogContent>
    </Dialog>
  )
}

export default DocumentDialog
